// client/guest/components/KindDetailsBlock.tsx
import React, { useEffect, useState } from "react";
import { TicketKind, isKindDisabled, fmtMoney } from "../utils";

type KindDetailsProps = {
  eventId?: number | null;
  kinds: TicketKind[];
  selectedKindId: number | null;
  currency: string;
  onSelectKind: (id: number) => void;
  onNext: () => void;
};

export default function KindDetailsBlock({
  eventId,
  kinds,
  selectedKindId,
  currency,
  onSelectKind,
  onNext,
}: KindDetailsProps) {
  const [selId, setSelId] = useState<number | null>(
    selectedKindId ?? (kinds[0]?.id ?? null)
  );

  useEffect(() => {
    if (selectedKindId != null) setSelId(selectedKindId);
    else if (!selId && kinds[0]) setSelId(kinds[0].id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedKindId, kinds.length]);

  const current = kinds.find((k) => k.id === selId) || null;
  const isLong = (s?: string | null) => (s?.trim()?.length ?? 0) > 18;

  return (
    <div className="flex-1 min-h-0 flex flex-col gap-3">
      <div className="grid gap-2 grid-cols-1 sm:grid-cols-2">
        {kinds.map((k) => {
          const disabled = isKindDisabled(k);
          const active = !disabled && k.id === current?.id;
          const span = !isLong(k.title) ? "" : "sm:col-span-2";

          const priceLabel = disabled
            ? "СКОРО"
            : k.subkinds?.length
            ? "Есть подвиды"
            : k.price_rub != null
            ? `${fmtMoney(k.price_rub)} ${currency}`
            : `0 ${currency}`;

          return (
            <button
              key={k.id}
              onClick={() => {
                if (!disabled) {
                  setSelId(k.id);
                  onSelectKind(k.id);
                }
              }}
              disabled={disabled}
              aria-disabled={disabled}
              className={`relative text-left rounded-2xl transition p-2 sm:p-2.5 ${span}
                ${
                  disabled
                    ? "bg-white/5 border border-white/10 text-white/45 cursor-not-allowed opacity-80 grayscale-[.35]"
                    : active
                    ? "bg-white/12 border border-white/20 text-white ring-1 ring-white/20"
                    : "bg-white/7 hover:bg-white/10 border border-white/10 text-white/90"
                }`}
            >
              <div
                className={`font-semibold leading-tight text-[14px] sm:text-[15px] ${
                  disabled ? "text-white/55 line-through" : "text-white"
                }`}
              >
                {k.title || "Без названия"}
              </div>

              <div
                className={`mt-0.5 text-[11px] sm:text-[12px] ${
                  disabled ? "text-white/45" : "text-white/75"
                }`}
              >
                {priceLabel}
              </div>

              {disabled && (
                <div className="absolute top-2 right-2 rounded-lg px-2 py-0.5 text-[10px] font-bold bg-white/10 text-white/70 border border-white/15">
                  СКОРО
                </div>
              )}
            </button>
          );
        })}
      </div>

      {/* карточка описания + кнопка */}
      <div className="flex-1 min-h-0 flex flex-col">
        <div className="rounded-2xl border border-white/10 bg-white/5 p-3 text-[13px] text-white/85 flex flex-col min-h-[140px] max-h-[300px] sm:min-h-[160px] sm:max-h-[320px] mb-3">
          <div className="flex items-center gap-2 mb-2 shrink-0">
            <span>🎟️</span>
            <span className="font-semibold text-white">
              Билет • {current?.title || "Без названия"}
            </span>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto whitespace-pre-wrap break-words pr-1">
            {current?.description?.trim() ? (
              current.description.trim()
            ) : (
              <span className="text-white/70">Описание отсутствует.</span>
            )}
          </div>
        </div>

        <button
          onClick={() => {
            if (!current || isKindDisabled(current)) return;
            onNext();
          }}
          disabled={!current || isKindDisabled(current)}
          className={`mt-auto w-full rounded-2xl px-4 py-3 font-semibold shadow-[inset_0_1px_0_rgba(255,255,255,.08)]
            ${
              !current || isKindDisabled(current)
                ? "bg-white/8 text-white/45 cursor-not-allowed"
                : "bg-emerald-600/95 hover:bg-emerald-500 text-white"
            }`}
        >
          {isKindDisabled(current)
            ? "СКОРО"
            : `Выбрать «${current?.title || "билет"}»`}
        </button>
      </div>
    </div>
  );
}